import { Controller, useFormContext } from 'react-hook-form';
import { useTheme } from '@mui/material';

import {
  assignEditableReferenceProperties,
  assignEditableScorecardProperties,
} from 'features/Connect/features/ConnectATSIntegration/utils';
import { ATSConnectHiringRequestCandidateFeedbackTestIds } from 'data-testids/ATS';
import { RowCTAContainer, StyledLink } from '../../../styles/styles';
import { RowCTAProps } from '../../../types/feedbackDetailTypes';
import { useDeleteReference } from 'features/References/api/referenceMutations';
import { useDeleteHiringRequestScorecard } from '../../../api/scorecardMutations';
import { useQueryGetScorecardsForHiringRequest } from 'features/Connect/features/ConnectATSIntegration/api/scorecardQueries';
import { CandidateFeedbackModalTypes } from '../../../enums';
import auth from 'utils/auth';
import { useState } from 'react';
import { DeletionModal } from 'sharedComponents/Modal/DeletionModal';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import { useQueryGetCompletedAndDraftReferences } from 'features/References/api/referenceQueries';

export const CurrentRowCTAModal = {
  DELETION: 'deletion',
  ERROR: 'error',
} as const;

export type CurrentRowCTAModalType = (typeof CurrentRowCTAModal)[keyof typeof CurrentRowCTAModal];

export const RowCTA: React.FC<RowCTAProps> = ({
  collectionId,
  isComplete,
  isPending,
  id,
  isAuthor,
  onEditClick,
  onViewClick,
  modalType,
}) => {
  const [currentModal, setCurrentModal] = useState<CurrentRowCTAModalType | null>(null);
  const { watch, setValue } = useFormContext();
  const candidateId = watch('candidate_id');
  const theme = useTheme();
  const districtId = auth.getUser()?.profile.district.id;
  const { data: completedScorecards } = useQueryGetScorecardsForHiringRequest(id, {
    enabled: modalType === CandidateFeedbackModalTypes.SCORECARD,
  });
  const { data: completedReferences } = useQueryGetCompletedAndDraftReferences(candidateId, {
    enabled: modalType === CandidateFeedbackModalTypes.REFERENCE,
  });

  const { mutate: deleteScorecard, error: requestError } = useDeleteHiringRequestScorecard(
    districtId,
    {
      onSuccess: () => {
        setCurrentModal(null);
      },
      onError: () => {
        setCurrentModal(null);
      },
    }
  );
  const requestErrorMessage = getAxiosErrorMessageByStatusCode(requestError);

  const { mutate: deleteReference, error: deleteError } = useDeleteReference(districtId, {
    onSuccess: () => {
      setCurrentModal(null);
    },
    onError: () => {
      setCurrentModal('error');
    },
  });

  const deleteErrorMessage = getAxiosErrorMessageByStatusCode(deleteError);

  const handleDeleteClick = () => {
    if (modalType === CandidateFeedbackModalTypes.SCORECARD) {
      deleteScorecard({ id, scorecardId: collectionId });
    } else {
      deleteReference({ referenceId: collectionId, candidateId });
    }
  };

  const handleEditScorecardClick = (_event, onChange) => {
    const completedScorecard = completedScorecards.find(
      (completedScorecard) => completedScorecard.id === collectionId
    );

    onChange(collectionId);
    onEditClick(collectionId);

    if (completedScorecard) {
      assignEditableScorecardProperties(completedScorecard, setValue);
    }
  };

  const handleViewScorecardClick = () => {
    const completedScorecard = completedScorecards.find(
      (completedScorecard) => completedScorecard.id === collectionId
    );

    onViewClick(collectionId);
    assignEditableScorecardProperties(completedScorecard, setValue);
  };

  const handleEditReferenceClick = () => {
    const completedReference = completedReferences.find(
      (completedReferences) => completedReferences.id === collectionId
    );

    onEditClick(collectionId);

    if (completedReference) {
      assignEditableReferenceProperties(completedReference, setValue);
    }
  };

  const handleViewReferenceClick = () => {
    const completedReference = completedReferences.find(
      (completedReferences) => completedReferences.id === collectionId
    );

    onViewClick(collectionId);
    assignEditableReferenceProperties(completedReference, setValue);
  };
  const handleOpenDeletionModal = () => setCurrentModal(CurrentRowCTAModal.DELETION);

  const errorMessage = deleteErrorMessage || requestErrorMessage;
  const isAuthorOrSuperAdmin = isAuthor || auth.isSuperAdminUser();
  const canDeleteFeedback = isAuthorOrSuperAdmin && !isPending;

  return (
    <RowCTAContainer>
      {modalType === CandidateFeedbackModalTypes.SCORECARD && isAuthorOrSuperAdmin && (
        <Controller
          name="completed_scorecard_id"
          render={({ field: { onChange } }) => (
            <StyledLink
              sx={{ paddingRight: theme.spacing(3) }}
              onClick={(event) => handleEditScorecardClick(event, onChange)}
              data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.REMIND_OR_EDIT_BUTTON}
            >
              {isComplete ? 'Edit' : 'Finish'}
            </StyledLink>
          )}
        />
      )}
      {modalType === CandidateFeedbackModalTypes.REFERENCE && canDeleteFeedback && (
        <StyledLink
          sx={{ paddingRight: theme.spacing(3) }}
          onClick={() => handleEditReferenceClick()}
          data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.REMIND_OR_EDIT_BUTTON}
        >
          Edit
        </StyledLink>
      )}
      {canDeleteFeedback && (
        <StyledLink
          sx={{ paddingRight: theme.spacing(3) }}
          onClick={handleOpenDeletionModal}
          data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.DELETE_BUTTON}
        >
          Delete
        </StyledLink>
      )}

      {!isPending && (
        <StyledLink
          sx={{ paddingRight: theme.spacing(3) }}
          data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.VIEW_BUTTON}
          onClick={
            modalType === CandidateFeedbackModalTypes.SCORECARD
              ? () => handleViewScorecardClick()
              : () => handleViewReferenceClick()
          }
        >
          View
        </StyledLink>
      )}

      <DeletionModal
        onClose={() => {
          setCurrentModal(null);
        }}
        onDelete={handleDeleteClick}
        open={currentModal === CurrentRowCTAModal.DELETION}
        text={`You will not be able to recover this ${modalType} once it is deleted.`}
        title="Are you sure?"
      />
      <ErrorSnackbar
        open={currentModal === CurrentRowCTAModal.ERROR}
        message={errorMessage}
        onClose={() => setCurrentModal(null)}
        dataTestId={ATSConnectHiringRequestCandidateFeedbackTestIds.ERROR_TOAST_ROW_CTA}
      />
    </RowCTAContainer>
  );
};
